import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import api from '../services/api';
import { PieChart, Pie, Cell, ResponsiveContainer, Legend, Tooltip } from 'recharts';
import './Dashboard.css';

const COLORS = {
  'todo': '#ef4444',
  'in-progress': '#f59e0b',
  'completed': '#10b981',
};

const Dashboard = () => {
  const [projects, setProjects] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [projectsRes, statsRes] = await Promise.all([
        api.get('/api/v1/projects'),
        api.get('/api/v1/projects/stats'),
      ]);
      setProjects(projectsRes.data.projects);
      setStats(statsRes.data.stats);
    } catch (err) {
      setError('Failed to load dashboard data');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this project?')) {
      return;
    }

    try {
      await api.delete(`/api/v1/projects/${id}`);
      fetchData();
    } catch (err) {
      alert('Failed to delete project');
    }
  };

  if (loading) {
    return (
      <div className="loading">
        <div className="spinner"></div>
      </div>
    );
  }

  if (error) {
    return <div className="error-message">{error}</div>;
  }

  const chartData = stats
    ? Object.entries(stats.byStatus).map(([status, count]) => ({
        name: status.charAt(0).toUpperCase() + status.slice(1).replace('-', ' '),
        value: count,
        color: COLORS[status],
      }))
    : [];

  const projectsByStatus = {
    todo: projects.filter((p) => p.status === 'todo'),
    'in-progress': projects.filter((p) => p.status === 'in-progress'),
    completed: projects.filter((p) => p.status === 'completed'),
  };

  return (
    <div className="dashboard">
      <div className="dashboard-header">
        <h1>Dashboard</h1>
        <Link to="/projects/new" className="btn btn-primary">
          + New Project
        </Link>
      </div>

      {stats && (
        <div className="stats-overview">
          <div className="stat-card">
            <h3>Total Projects</h3>
            <p className="stat-number">{stats.total}</p>
          </div>
          <div className="stat-card">
            <h3>Completed</h3>
            <p className="stat-number" style={{ color: COLORS.completed }}>
              {stats.byStatus.completed}
            </p>
          </div>
          <div className="stat-card">
            <h3>In Progress</h3>
            <p className="stat-number" style={{ color: COLORS['in-progress'] }}>
              {stats.byStatus['in-progress']}
            </p>
          </div>
          <div className="stat-card">
            <h3>Todo</h3>
            <p className="stat-number" style={{ color: COLORS.todo }}>
              {stats.byStatus.todo}
            </p>
          </div>
        </div>
      )}

      {chartData.length > 0 && (
        <div className="card">
          <h2>Project Status Distribution</h2>
          <ResponsiveContainer width="100%" height={300}>
            <PieChart>
              <Pie
                data={chartData}
                cx="50%"
                cy="50%"
                labelLine={false}
                label={({ name, percent }) => `${name}: ${(percent * 100).toFixed(0)}%`}
                outerRadius={80}
                fill="#8884d8"
                dataKey="value"
              >
                {chartData.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={entry.color} />
                ))}
              </Pie>
              <Tooltip />
              <Legend />
            </PieChart>
          </ResponsiveContainer>
        </div>
      )}

      <div className="projects-by-status">
        {Object.entries(projectsByStatus).map(([status, statusProjects]) => (
          <div key={status} className="status-section">
            <h2>
              {status.charAt(0).toUpperCase() + status.slice(1).replace('-', ' ')} (
              {statusProjects.length})
            </h2>
            {statusProjects.length === 0 ? (
              <p className="empty-state">No projects in this status</p>
            ) : (
              <div className="project-grid">
                {statusProjects.map((project) => (
                  <div key={project.id} className="project-card">
                    {project.image_url && (
                      <img
                        src={project.image_url}
                        alt={project.name}
                        className="project-image"
                      />
                    )}
                    <div className="project-content">
                      <h3>
                        <Link to={`/projects/${project.id}`}>{project.name}</Link>
                      </h3>
                      {project.description && <p>{project.description}</p>}
                      <div className="project-meta">
                        <span className="progress-badge">
                          {project.progress_percentage}%
                        </span>
                        <div className="project-actions">
                          <Link
                            to={`/projects/${project.id}/edit`}
                            className="btn-link"
                          >
                            Edit
                          </Link>
                          <button
                            onClick={() => handleDelete(project.id)}
                            className="btn-link btn-danger-link"
                          >
                            Delete
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
};

export default Dashboard;
